import { z } from "zod";
import { v4 } from "uuid";

export enum ExperimentStatus {
  EXPERIMENT_STATUS_UNSPECIFIED = 0,
  EXPERIMENT_STATUS_DRAFT = 1,
  EXPERIMENT_STATUS_RUNNING = 2,
  EXPERIMENT_STATUS_PAUSED = 3,
  EXPERIMENT_STATUS_ARCHIVED = 4,
}

export enum VariantType {
  VARIANT_TYPE_UNSPECIFIED = 0,
  VARIANT_TYPE_SCENARIO = 1,
  VARIANT_TYPE_URL = 2,
}

export enum VariantRole {
  VARIANT_ROLE_UNSPECIFIED = 0,
  VARIANT_ROLE_CONTROL = 1,
  VARIANT_ROLE_VARIANT = 2,
}

export enum Verdict {
  VERDICT_UNSPECIFIED = 0,
  VERDICT_COLLECTING = 1,
  VERDICT_NO_WINNER = 2,
  VERDICT_WINNER_A = 3,
  VERDICT_WINNER_B = 4,
}

export enum DiffUnit {
  DIFF_UNIT_UNSPECIFIED = 0,
  DIFF_UNIT_PERCENT = 1,
}

/** Bucket size of a `TimeSeriesPoint`, carried per point so a future mixed-granularity series
 *  needs no new field. Integer values are proto-verified to match 1:1 (Business Rule 3). */
export enum Granularity {
  GRANULARITY_UNSPECIFIED = 0,
  GRANULARITY_DAY = 1,
  GRANULARITY_WEEK = 2,
}

const TICKER_REGEX = /^[a-zA-Z0-9_]+$/;
// The variant's `ticker` is a free-text display label (shown as-is in results, e.g.
// ScenarioVsCell/ExperimentEvolutionChart's legend) - unlike the experiment/scenario ticker above,
// it is never used as a system identifier, so it only needs to exclude control characters, not be
// slug-safe. Letters (any language/accents), digits, underscores, and spaces cover both the
// original underscore-separated convention (e.g. "motor_sin_display") and free text like "Motor
// para navidades de invierno".
const VARIANT_NAME_REGEX = /^[\p{L}\p{N}_ ]+$/u;

export const VariantSchema = z
  .object({
    id: z.string().uuid(),
    ticker: z.string().min(1).max(50).regex(VARIANT_NAME_REGEX),
    type: z.nativeEnum(VariantType).default(VariantType.VARIANT_TYPE_SCENARIO),
    scenarioId: z.string().uuid().nullable().default(null),
    url: z.string().nullable().default(null),
    trafficSplit: z.number().int().min(0).max(100).default(50),
    scenarioTicker: z.string().nullable().default(null),
    role: z.nativeEnum(VariantRole).default(VariantRole.VARIANT_ROLE_UNSPECIFIED),
    isEngineDefault: z.boolean().default(false),
  })
  .superRefine((variant, ctx) => {
    if (variant.type === VariantType.VARIANT_TYPE_SCENARIO) {
      if (variant.scenarioId === null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["scenarioId"],
          message: "SCENARIO variant must have scenarioId and no url",
        });
      }
      if (variant.url !== null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["url"],
          message: "SCENARIO variant must have scenarioId and no url",
        });
      }
    } else if (variant.type === VariantType.VARIANT_TYPE_URL) {
      if (variant.url === null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["url"],
          message: "URL variant must have url and no scenarioId",
        });
      }
      if (variant.scenarioId !== null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["scenarioId"],
          message: "URL variant must have url and no scenarioId",
        });
      }
    }
  });

export type VariantInput = z.input<typeof VariantSchema>;
export type VariantEntity = z.output<typeof VariantSchema>;

// Base shape kept separate from its business-rule refinement below so that
// `.omit()` can still be used to derive `ExperimentCreateInputSchema` — zod does not
// allow `.omit()` on a schema that already carries a `.superRefine()`.
const ExperimentBaseSchema = z.object({
  id: z.string().uuid(),
  ticker: z.string().min(1).max(50).regex(TICKER_REGEX),
  description: z.string().max(500).nullable().default(null),
  status: z.nativeEnum(ExperimentStatus).default(ExperimentStatus.EXPERIMENT_STATUS_DRAFT),
  targetPath: z.string().min(1).default("/"),
  // Required (Business Rule 1) - the backend's sole source of truth for the experiment's start
  // instant, drives DRAFT/RUNNING resolution server-side. Caller-supplied at creation (like the
  // old, now-removed `startDate`), not server-assigned.
  startedAt: z.date(),
  estimatedEndDate: z.date().nullable().default(null),
  variants: z.tuple([VariantSchema, VariantSchema]),
  // Minimum detectable effect, as a RELATIVE improvement in percent (20 => "I want to be able to
  // detect a 20% relative lift", e.g. 3.6% -> 4.32%). Drives the backend's required_visitors /
  // sample_progress. The proto treats 0 as "unset" and applies its own default (20); the form
  // pre-fills the same 20 so the user always sees the value that will actually be used.
  mde: z.number().int().min(0).max(100).default(20),
  // Minimum exposure days before any winner may be declared, regardless of sample size (weekly
  // seasonality). Same 0-is-unset / default-14 convention as `mde`.
  minDays: z.number().int().min(0).max(365).default(14),
  createdAt: z.date().nullable().default(null),
  updatedAt: z.date().nullable().default(null),
  deletedAt: z.date().nullable().default(null),
});

function checkTrafficSplitSumsTo100(
  experiment: { variants: [{ trafficSplit: number }, { trafficSplit: number }] },
  ctx: z.RefinementCtx
) {
  const totalTrafficSplit =
    experiment.variants[0].trafficSplit + experiment.variants[1].trafficSplit;
  if (totalTrafficSplit !== 100) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["variants"],
      message: "Traffic split across both variants must sum to 100",
    });
  }
}

export const ExperimentSchema = ExperimentBaseSchema.superRefine(checkTrafficSplitSumsTo100);

export type ExperimentInput = z.input<typeof ExperimentSchema>;
export type ExperimentEntity = z.output<typeof ExperimentSchema>;

export const ExperimentCreateInputSchema = ExperimentBaseSchema.omit({
  id: true,
  status: true,
  createdAt: true,
  updatedAt: true,
  deletedAt: true,
}).superRefine(checkTrafficSplitSumsTo100);
export type ExperimentCreateInput = z.input<typeof ExperimentCreateInputSchema>;

export const ExperimentListItemSchema = z.object({
  experiment: ExperimentSchema,
  lift: z.number(),
  hasLift: z.boolean(),
  // 0..100 on this entity. NOTE the deliberate asymmetry with `ExperimentResults.confidence`,
  // which the same backend sends as a FRACTION in [0,1] — both are carried through verbatim and
  // converted at the point of display (see this change's design.md, Decision 2).
  confidence: z.number(),
  isReliable: z.boolean(),
  activeDays: z.number().int().min(0),
  liftTooltipKey: z.string(),
  // Collected valid visitors / required valid visitors, as a fraction (0.28 = 28%). Not clamped
  // at 1. 0 when the row has no trustworthy metrics (same condition as `hasLift === false`).
  sampleProgress: z.number(),
});
export type ExperimentListItem = z.output<typeof ExperimentListItemSchema>;

export interface ExperimentFilters {
  tickerContains?: string;
  status?: ExperimentStatus;
  startedAtFrom?: Date;
  startedAtTo?: Date;
  minDurationDays?: number;
}

export const METRIC_RANGES = ["LAST_7", "LAST_15", "LAST_30", "FULL", "CUSTOM"] as const;
export type MetricRange = typeof METRIC_RANGES[number];

export interface DateRange {
  from: Date;
  to: Date;
}

export const ScenarioMetricsSchema = z.object({
  variantId: z.string().uuid(),
  variantTicker: z.string(),
  role: z.nativeEnum(VariantRole),
  visits: z.number().int().min(0),
  bookings: z.number().int().min(0),
  conversionRate: z.number(),
  revenueTotal: z.number(),
  revenuePerVisit: z.number(),
  adr: z.number(),
  avgBookingValue: z.number(),
  abandonmentRate: z.number(),
  currency: z.string(),
  // Denominator actually used for `conversionRate`. Equal to `visits` for a warehouse-sourced
  // result (the adapter puts valid_visitors into both).
  validVisitors: z.number().int().min(0),
  // `visits - validVisitors`: staff previews, operator traffic and suspected bots. Display only,
  // never a denominator.
  excludedVisitors: z.number().int().min(0),
  // Wilson 95% bounds of `conversionRate`, on the same already-scaled-by-100 percent scale.
  ciLow: z.number(),
  ciHigh: z.number(),
  // Subset of `excludedVisitors`.
  botSuspectVisitors: z.number().int().min(0),
});
export type ScenarioMetricsEntity = z.output<typeof ScenarioMetricsSchema>;

export const MetricComparisonSchema = z.object({
  metricKey: z.string(),
  valueA: z.number(),
  valueB: z.number(),
  winner: z.union([z.literal("A"), z.literal("B"), z.literal("")]),
  relativeDiff: z.number().nullable(),
  diffUnit: z.nativeEnum(DiffUnit),
  higherIsBetter: z.boolean(),
});
export type MetricComparisonEntity = z.output<typeof MetricComparisonSchema>;

export const TimeSeriesMetricValueSchema = z.object({
  a: z.number().nullable(),
  b: z.number().nullable(),
  // Wilson 95% bounds of the cumulative value at this point, per scenario. The backend only
  // populates these for "conversion_rate" (the one metric with a proportion to bracket) — they are
  // null everywhere else, which is exactly how the chart knows whether to draw a confidence band.
  ciLowA: z.number().nullable(),
  ciHighA: z.number().nullable(),
  ciLowB: z.number().nullable(),
  ciHighB: z.number().nullable(),
});
export type TimeSeriesMetricValueEntity = z.output<typeof TimeSeriesMetricValueSchema>;

export const TimeSeriesPointSchema = z.object({
  bucketStart: z.date(),
  labelKey: z.string(),
  metrics: z.record(z.string(), TimeSeriesMetricValueSchema),
  // Explicit bucket size, so the chart labels its granularity from the data instead of re-deriving
  // it from the requested range.
  granularity: z.nativeEnum(Granularity),
  // Exclusive end of the bucket; null when the backend did not send one. Lets the chart place a
  // pause marker in the bucket that actually contains it.
  bucketEnd: z.date().nullable(),
});
export type TimeSeriesPointEntity = z.output<typeof TimeSeriesPointSchema>;

export const SegmentMetricsSchema = z.object({
  dimension: z.string(), // "device" | "origin_class"
  value: z.string(), // e.g. "MOBILE", "direct"
  scenarioA: ScenarioMetricsSchema,
  scenarioB: ScenarioMetricsSchema,
  // FRACTION in [0,1], like `ExperimentResults.confidence` (and unlike the list item's 0..100).
  confidence: z.number(),
  liftPct: z.number(), // relative lift of B over A, in percent
  smallSample: z.boolean(), // combined valid visitors < 500 — the backend decides, not the client
});
export type SegmentMetricsEntity = z.output<typeof SegmentMetricsSchema>;

export const PausePeriodSchema = z.object({
  from: z.date(),
  /** `null` means the experiment is still paused (epoch-ms `0` on the wire). */
  to: z.date().nullable(),
});
export type PausePeriodEntity = z.output<typeof PausePeriodSchema>;

export const ExperimentResultsSchema = z.object({
  experiment: ExperimentSchema,
  verdict: z.nativeEnum(Verdict),
  scenarioA: ScenarioMetricsSchema,
  scenarioB: ScenarioMetricsSchema,
  table: z.array(MetricComparisonSchema),
  series: z.array(TimeSeriesPointSchema),
  activeDays: z.number().int().min(0),
  remainingDays: z.number().int().min(0),
  hasRemainingDays: z.boolean(),
  // Live, not deprecated: `minBookingsReached` IS the decision panel's bookings gate
  // (`resolveDecisionGates`), and `minBookingsThreshold` is the figure its label states.
  // `backoffice-manager` keeps sending both, with the 50-bookings-per-arm value.
  minBookingsReached: z.boolean(),
  minBookingsThreshold: z.number().int().min(0),
  partial: z.boolean(),
  warnings: z.array(z.string()),

  // --- Statistical decision support -----------------------------------------------------------
  // Scale conventions, preserved exactly as the wire sends them (design.md Decision 2):
  //   * FRACTIONS in [0,1]: confidence, pValue, srmPValue, expectedShareA, observedShareA,
  //     sampleProgress (the last one deliberately NOT clamped at 1).
  //   * ALREADY SCALED BY 100 (percent): lift, liftCiLow, liftCiHigh — matching conversionRate
  //     and ScenarioMetrics.ciLow/ciHigh.
  confidence: z.number(), // 1 - p of the two-proportion z-test
  pValue: z.number(),
  srmPValue: z.number(),
  srmAlert: z.boolean(), // srmPValue < 0.01 => the observed split is not compatible with the configured one
  expectedShareA: z.number(),
  observedShareA: z.number(),
  requiredVisitors: z.number().int().min(0), // valid visitors needed IN TOTAL (both arms)
  sampleProgress: z.number(),
  estimatedDaysLeft: z.number().int(),
  hasEstimatedDaysLeft: z.boolean(), // false => no honest estimate; show nothing rather than a guess
  mde: z.number().int(), // the mde actually applied (echoes Experiment.mde, or the backend default)
  minDays: z.number().int(),
  /** Last fully-loaded hotel-local day, ISO "YYYY-MM-DD"; null when unknown (`""` on the wire). */
  dataUntil: z.string().nullable(),
  /** "warehouse" | "witbooking-api" | "fake"; null when unknown (`""` on the wire). */
  source: z.string().nullable(),
  /** Computed by the backend — never re-derived from `verdict` in the client. */
  isReliable: z.boolean(),
  segments: z.array(SegmentMetricsSchema),
  pauses: z.array(PausePeriodSchema),
  lift: z.number(), // relative lift of B over A, in percent
  liftCiLow: z.number(),
  liftCiHigh: z.number(),
  hasLiftCi: z.boolean(), // false => the interval is not computable; hide the error bar
});
export type ExperimentResults = z.output<typeof ExperimentResultsSchema>;

export function createEmptyExperiment(id: string): ExperimentEntity {
  return {
    id,
    ticker: "",
    description: null,
    status: ExperimentStatus.EXPERIMENT_STATUS_DRAFT,
    targetPath: "/",
    startedAt: new Date(),
    estimatedEndDate: null,
    mde: 20,
    minDays: 14,
    variants: [
      {
        id: v4(),
        ticker: "",
        type: VariantType.VARIANT_TYPE_SCENARIO,
        scenarioId: null,
        url: null,
        trafficSplit: 50,
        scenarioTicker: null,
        role: VariantRole.VARIANT_ROLE_CONTROL,
        isEngineDefault: false,
      },
      {
        id: v4(),
        ticker: "",
        type: VariantType.VARIANT_TYPE_SCENARIO,
        scenarioId: null,
        url: null,
        trafficSplit: 50,
        scenarioTicker: null,
        role: VariantRole.VARIANT_ROLE_VARIANT,
        isEngineDefault: false,
      },
    ],
    createdAt: null,
    updatedAt: null,
    deletedAt: null,
  };
}
